import { Box } from '@/components/ui/lib/box';
import { VStack } from '@/components/ui/lib/vstack';
import { Image } from '@/components/ui/lib/image';
import { Heading } from '@/components/ui/lib/heading';
import { Text } from '@/components/ui/lib/text';
import { Link } from '@/components/ui/lib/link';
import { DICTIONARY } from '@/config/dictionary';
import { env } from '@/config/env';
import { useAppStore } from '@/stores/app-store';
import { APP_PATHS } from '@/config/routes/app-paths';
import { ensureValidPath } from '@/utils/validate-path';
import type { ErrorPageLayoutProps } from '@/components/ui/types/error-page-layout';

const ErrorPageLayout = ({
  heading,
  subheading,
  description,
  actions,
  devMessage,
  fullscreen = false,
}: ErrorPageLayoutProps) => {
  const colorMode = useAppStore((s) => s.colorMode);
  const logoSrc =
    colorMode === 'dark' && env.VITE_LOGO_URL_DARK ? env.VITE_LOGO_URL_DARK : env.VITE_LOGO_URL;

  return (
    <Box
      {...(fullscreen ? { position: 'fixed', inset: '0', zIndex: '9999' } : { minH: '100vh' })}
      display="flex"
      flexDirection="column"
      className="bg-page text-base"
    >
      {/* Minimal header — logo only, no nav */}
      <Box as="header" px="6" py="5" className="border-b border-rim">
        <Link href={ensureValidPath(APP_PATHS.home)} _hover={{ textDecoration: 'none' }}>
          <Image src={logoSrc} alt={DICTIONARY.brand.full} h="8" />
        </Link>
      </Box>

      {/* Centred content */}
      <Box display="flex" flex="1" alignItems="center" justifyContent="center" p="8">
        <VStack gap="4" maxW="md" textAlign="center">
          <Heading size="4xl" fontWeight="700" className="text-accent">
            {heading}
          </Heading>
          {subheading && (
            <Heading size="lg" fontWeight="600">
              {subheading}
            </Heading>
          )}
          <Text color="text-muted">{description}</Text>

          {devMessage && (
            <Box
              bg="red.50"
              _dark={{ bg: 'red.950', borderColor: 'red.800' }}
              border="1px solid"
              borderColor="red.200"
              borderRadius="md"
              p="3"
              w="full"
              textAlign="left"
            >
              <Text fontSize="xs" fontFamily="mono" color="red.700" _dark={{ color: 'red.300' }}>
                {devMessage}
              </Text>
            </Box>
          )}

          {actions}

          <Text fontSize="sm" color="text-muted" pt="2">
            {DICTIONARY.errors.contactPrompt}{' '}
            <Link
              href={`mailto:${DICTIONARY.errors.contactEmail}`}
              className="text-accent"
              _hover={{ textDecoration: 'underline' }}
            >
              {DICTIONARY.errors.contactEmail}
            </Link>
          </Text>
        </VStack>
      </Box>
    </Box>
  );
};

export default ErrorPageLayout;
